import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';

const ACCENT = '#10B981';
const AMBER = '#F59E0B';
const CYAN = '#06B6D4';
const TEXT = 'var(--text)';
const MUTED = 'var(--muted)';
const BORDER = 'var(--border)';
const BG2 = 'var(--bg2)';

type Urgency = 'URGENT' | 'HIGH' | 'MEDIUM' | 'PLAN' | 'BIG';

type Festival = {
  id: string;
  name: string;
  dateLabel: string;
  emoji: string;
  date: string;
  urgency: Urgency;
};

const FESTIVALS: Festival[] = [
  { id: 'eid-fitr', name: 'Eid ul-Fitr', dateLabel: 'April 2026', emoji: '🌙', date: '2026-04-16', urgency: 'URGENT' },
  { id: 'akshaya', name: 'Akshaya Tritiya', dateLabel: '30 Apr 2026', emoji: '✨', date: '2026-04-30', urgency: 'HIGH' },
  { id: 'mothers', name: "Mother's Day", dateLabel: '11 May 2026', emoji: '💐', date: '2026-05-11', urgency: 'MEDIUM' },
  { id: 'eid-adha', name: 'Eid ul-Adha', dateLabel: '7 Jun 2026', emoji: '🐑', date: '2026-06-07', urgency: 'PLAN' },
  { id: 'fathers', name: "Father's Day", dateLabel: '21 Jun 2026', emoji: '👔', date: '2026-06-21', urgency: 'PLAN' },
  { id: 'rakhi', name: 'Raksha Bandhan', dateLabel: '9 Aug 2026', emoji: '🪢', date: '2026-08-09', urgency: 'PLAN' },
  { id: 'independence', name: 'Independence Day', dateLabel: '15 Aug 2026', emoji: '🇮🇳', date: '2026-08-15', urgency: 'PLAN' },
  { id: 'janmashtami', name: 'Janmashtami', dateLabel: '16 Aug 2026', emoji: '🦚', date: '2026-08-16', urgency: 'PLAN' },
  { id: 'ganesh', name: 'Ganesh Chaturthi', dateLabel: '22 Aug 2026', emoji: '🐘', date: '2026-08-22', urgency: 'PLAN' },
  { id: 'navratri', name: 'Navratri', dateLabel: '2 Oct 2026', emoji: '🪔', date: '2026-10-02', urgency: 'PLAN' },
  { id: 'dussehra', name: 'Dussehra', dateLabel: '12 Oct 2026', emoji: '🏹', date: '2026-10-12', urgency: 'PLAN' },
  { id: 'diwali', name: 'Diwali', dateLabel: '20 Oct 2026', emoji: '🪔', date: '2026-10-20', urgency: 'BIG' },
];

function startOfDay(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function daysUntil(iso: string): number {
  const target = startOfDay(new Date(iso + 'T12:00:00'));
  const today = startOfDay(new Date());
  return Math.ceil((target - today) / 86400000);
}

function urgencyStyle(u: Urgency): { bg: string; color: string; label: string } {
  switch (u) {
    case 'URGENT':
      return { bg: 'rgba(239,68,68,0.2)', color: '#F87171', label: 'URGENT' };
    case 'HIGH':
      return { bg: `${AMBER}28`, color: '#FBBF24', label: 'HIGH' };
    case 'MEDIUM':
      return { bg: 'rgba(6,182,212,0.2)', color: '#67E8F9', label: 'MEDIUM' };
    case 'BIG':
      return { bg: `${ACCENT}28`, color: '#6EE7B7', label: 'BIG' };
    default:
      return { bg: BG2, color: 'var(--muted)', label: 'PLAN' };
  }
}

const CALENDAR_MONTHS: { month: string; dot: string; festivals: string }[] = [
  { month: 'Jan', dot: ACCENT, festivals: 'Makar Sankranti, Republic Day' },
  { month: 'Feb', dot: CYAN, festivals: "Valentine's Day, Basant Panchami" },
  { month: 'Mar', dot: AMBER, festivals: 'Holi, Ugadi' },
  { month: 'Apr', dot: '#EC4899', festivals: 'Ram Navami, Eid, Baisakhi, Akshaya Tritiya' },
  { month: 'May', dot: ACCENT, festivals: "Mother's Day, Buddha Purnima" },
  { month: 'Jun', dot: CYAN, festivals: "Eid ul-Adha, Father's Day, Yoga Day" },
  { month: 'Jul', dot: AMBER, festivals: 'Muharram, Guru Purnima' },
  { month: 'Aug', dot: '#F97316', festivals: 'Independence Day, Raksha Bandhan, Janmashtami, Onam' },
  { month: 'Sep', dot: ACCENT, festivals: 'Ganesh Chaturthi, Navratri' },
  { month: 'Oct', dot: '#8B5CF6', festivals: 'Navratri, Dussehra, Diwali' },
  { month: 'Nov', dot: CYAN, festivals: 'Diwali (if late), Guru Nanak Jayanti, Children\'s Day' },
  { month: 'Dec', dot: '#EF4444', festivals: 'Christmas, New Year prep' },
];

export function FestivePage() {
  const [industry, setIndustry] = useState('Real Estate');
  const [city, setCity] = useState('Pan India');
  const [selectedFestival, setSelectedFestival] = useState(FESTIVALS[11].name);
  const [checks, setChecks] = useState<Record<string, boolean>>({
    social: false,
    wa: false,
    email: false,
    ads: false,
    sms: false,
    offer: false,
  });
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(false);
  const [openMonth, setOpenMonth] = useState<string | null>('Oct');

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await api.get<{
          business: { business_type: string | null; city: string | null } | null;
        }>('/business/me');
        if (data.business) {
          if (data.business.business_type) setIndustry(data.business.business_type);
          if (data.business.city) setCity(data.business.city);
        }
      } catch {
        /* defaults */
      }
    })();
  }, []);

  const festivalDays = FESTIVALS.map((f) => ({
    ...f,
    daysLeft: daysUntil(f.date),
  }));

  const generate = useCallback(async () => {
    setGenerating(true);
    setPreview(false);
    await new Promise((r) => setTimeout(r, 2000));
    setPreview(true);
    setGenerating(false);
  }, []);

  const festEmoji = FESTIVALS.find((f) => f.name === selectedFestival)?.emoji ?? '🪔';

  return (
    <div className="odc-page relative z-10 space-y-8">
      <style>{`
        @keyframes odc-border-pulse {
          0%, 100% { box-shadow: 0 0 0 1px rgba(239,68,68,0.5); }
          50% { box-shadow: 0 0 0 3px rgba(239,68,68,0.35); }
        }
      `}</style>

      <header
        className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between"
        style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 24 }}
      >
        <div className="min-w-0 flex-1 space-y-3">
          <span
            className="inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{ background: `${AMBER}28`, color: '#FCD34D', border: `1px solid ${AMBER}55` }}
          >
            FESTIVE AI
          </span>
          <h1 className="text-2xl font-bold md:text-3xl" style={{ color: TEXT }}>
            Festive Surge Predictor
          </h1>
          <p className="max-w-xl text-sm leading-relaxed" style={{ color: MUTED }}>
            Advance campaigns for 50+ Indian festivals — AI prepares 30 days in advance
          </p>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {['50+ Indian Festivals', '30 Days Advance', 'Auto-Launch'].map((t) => (
            <span
              key={t}
              className="shrink-0 rounded-full border px-3 py-2 text-[11px] font-semibold"
              style={{ borderColor: `${AMBER}44`, background: `${AMBER}14`, color: '#FCD34D' }}
            >
              {t}
            </span>
          ))}
        </div>
      </header>

      <section>
        <h2 className="mb-4 text-lg font-bold" style={{ color: TEXT }}>
          Upcoming Festivals — Next 90 Days
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {festivalDays.map((f) => {
            const u = urgencyStyle(f.urgency);
            const urgentBorder = f.daysLeft <= 7 && f.urgency === 'URGENT';
            const amberBorder = f.daysLeft <= 30 && f.daysLeft > 7;
            return (
              <div
                key={f.id}
                className="odc-card flex flex-col !p-5"
                style={{
                  transform: 'none',
                  borderColor: urgentBorder ? '#EF4444' : amberBorder ? `${AMBER}88` : BORDER,
                  animation: urgentBorder ? 'odc-border-pulse 2s ease-in-out infinite' : undefined,
                }}
              >
                <div className="text-[40px] leading-none">{f.emoji}</div>
                <h3 className="mt-2 text-base font-bold" style={{ color: TEXT }}>
                  {f.name}
                </h3>
                <p className="text-xs" style={{ color: MUTED }}>
                  {f.dateLabel}
                </p>
                <p
                  className="mt-2 text-sm font-bold"
                  style={{
                    color:
                      f.daysLeft <= 7 ? '#F87171' : f.daysLeft <= 30 ? AMBER : ACCENT,
                  }}
                >
                  {f.daysLeft < 0 ? 'Passed' : `${f.daysLeft} days left`}
                </p>
                <span
                  className="mt-2 w-fit rounded-md px-2 py-0.5 text-[9px] font-black uppercase"
                  style={{ background: u.bg, color: u.color }}
                >
                  {u.label}
                </span>
                <button type="button" className="odc-btn-ghost mt-4 w-full justify-center !py-2 !text-xs">
                  Plan Campaign
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-bold" style={{ color: TEXT }}>
          AI Campaign Planner
        </h2>
        <p className="mt-1 text-sm" style={{ color: MUTED }}>
          Select a festival → AI generates complete campaign
        </p>
        <div className="mt-6 grid gap-6 lg:grid-cols-[3fr_2fr]">
          <div className="odc-card space-y-4 !p-6" style={{ transform: 'none' }}>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
                Select Festival
              </span>
              <select
                className="odc-select mt-1.5 w-full !py-2.5"
                value={selectedFestival}
                onChange={(e) => setSelectedFestival(e.target.value)}
              >
                {FESTIVALS.map((x) => (
                  <option key={x.id} value={x.name}>
                    {x.emoji} {x.name}
                  </option>
                ))}
              </select>
            </label>
            <div
              className="rounded-xl border p-4 text-sm"
              style={{ borderColor: BORDER, background: BG2 }}
            >
              <p style={{ color: MUTED }}>
                <span style={{ color: TEXT, fontWeight: 600 }}>Industry:</span> {industry}
              </p>
              <p className="mt-1" style={{ color: MUTED }}>
                <span style={{ color: TEXT, fontWeight: 600 }}>Target:</span> {city}
              </p>
            </div>
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
                Campaign elements
              </p>
              {(
                [
                  { key: 'social', label: 'Social Media Posts (5 posts)' },
                  { key: 'wa', label: 'WhatsApp Broadcast message' },
                  { key: 'email', label: 'Email campaign' },
                  { key: 'ads', label: 'Google/Meta Ad copy' },
                  { key: 'sms', label: 'SMS message' },
                  { key: 'offer', label: 'Offer/discount suggestion' },
                ] as const
              ).map((c) => (
                <label
                  key={c.key}
                  className="mb-2 flex cursor-pointer items-center gap-2 text-sm"
                  style={{ color: TEXT }}
                >
                  <input
                    type="checkbox"
                    checked={checks[c.key]}
                    onChange={(e) => setChecks((s) => ({ ...s, [c.key]: e.target.checked }))}
                    className="rounded border"
                    style={{ accentColor: ACCENT }}
                  />
                  {c.label}
                </label>
              ))}
            </div>
            <button
              type="button"
              className="odc-btn-primary w-full justify-center !py-3"
              disabled={generating}
              onClick={() => void generate()}
            >
              {generating ? `Generating your ${selectedFestival} campaign... ${festEmoji}` : '🎉 Generate Festival Campaign'}
            </button>
          </div>

          <div className="odc-card !flex !min-h-[280px] !flex-col !p-6" style={{ transform: 'none' }}>
            <h3 className="text-base font-bold" style={{ color: TEXT }}>
              Campaign Preview
            </h3>
            {!preview ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
                <span className="text-5xl">{festEmoji}</span>
                <p className="text-sm font-medium" style={{ color: TEXT }}>
                  Select a festival and generate campaign
                </p>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {(
                  [
                    { icon: '📱', text: `Instagram Post: Happy ${selectedFestival} from [Business]! ${festEmoji} This festive season...` },
                    { icon: '💬', text: `${selectedFestival} Mubarak! Special offer just for you...` },
                    { icon: '📧', text: `Email Subject: ✨ ${selectedFestival} Special — Exclusive offer inside!` },
                    { icon: '📢', text: `Celebrate ${selectedFestival} with [Business]. Limited time offer!` },
                  ] as const
                ).map((row) => (
                  <div
                    key={row.icon}
                    className="rounded-xl border p-3 text-xs leading-relaxed"
                    style={{ borderColor: BORDER, background: BG2, color: MUTED }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span>
                        {row.icon} {row.text}
                      </span>
                      <button type="button" className="odc-btn-ghost shrink-0 !px-2 !py-1 !text-[10px]">
                        Copy
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="odc-card !p-6" style={{ transform: 'none' }}>
        <h2 className="text-lg font-bold" style={{ color: TEXT }}>
          Annual Festival Calendar
        </h2>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {CALENDAR_MONTHS.map((m) => {
            const open = openMonth === m.month;
            return (
              <button
                key={m.month}
                type="button"
                onClick={() => setOpenMonth(open ? null : m.month)}
                className="rounded-xl border p-4 text-left transition"
                style={{
                  borderColor: open ? ACCENT : BORDER,
                  background: open ? `${ACCENT}0d` : 'var(--card)',
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: m.dot }} />
                  <span className="font-bold" style={{ color: TEXT }}>
                    {m.month}
                  </span>
                  <span className="text-xs" style={{ color: MUTED }}>
                    {open ? '▼' : '▶'}
                  </span>
                </div>
                {open ? <p className="mt-2 text-xs leading-relaxed" style={{ color: MUTED }}>{m.festivals}</p> : null}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
